/**
 * NotificationPreferencesRouter — Iter-273
 * Smart router for the email-footer "Email preferences" link.
 *
 * - If the user is signed in, redirect to their role-specific settings page.
 * - If not signed in, send them to /login with a `next=` hint so the link
 *   resumes after authentication.
 *
 * Why this exists: email templates ship with a STATIC link
 * (https://app.ievntur.com/notifications/preferences). We can't know the
 * recipient's role at email-build time, so the email link always points
 * here and we forward at click-time once we know who they are.
 */
import React, { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Loader2 } from 'lucide-react';

const ROLE_TARGET = {
  talent:      '/talent/notification-settings',
  client:      '/client/notification-settings',
  admin:       '/admin/notification-settings',
  super_admin: '/admin/notification-settings',
  agency:      '/agency/settings',
};

const NotificationPreferencesRouter = () => {
  const { user, loading } = useAuth();

  useEffect(() => {
    // Log so we can see in browser console + Sentry which role landed here.
    if (user?.role) {
      // eslint-disable-next-line no-console
      console.info(`[NotificationPrefs] redirecting role=${user.role}`);
    }
  }, [user]);

  if (loading) {
    return (
      <div
        className="flex items-center justify-center min-h-screen text-slate-500"
        data-testid="notif-prefs-loading"
      >
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        Loading preferences…
      </div>
    );
  }

  if (!user) {
    return (
      <Navigate
        to={`/login?next=${encodeURIComponent('/notifications/preferences')}`}
        replace
        data-testid="notif-prefs-redirect-login"
      />
    );
  }

  const target = ROLE_TARGET[user.role] || '/talent/notification-settings';
  return <Navigate to={target} replace data-testid="notif-prefs-redirect" />;
};

export default NotificationPreferencesRouter;
